import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@/components/icons";
import { usePopoverPosition } from "@/components/use-popover-position";

type Props = {
  /**
   * Current value as typed: YYYY-MM-DD, or a partial YYYY-MM / YYYY (or empty).
   * Picking from the calendar always yields a full YYYY-MM-DD.
   */
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  /**
   * Maximum selectable date (inclusive). Defaults to today — used by the DOB
   * field on screening / monitoring / settings to prevent future birthdays.
   * Pass `undefined` (not omitted) to disable the limit if a use case ever
   * needs forward dates.
   */
  maxDate?: Date | null;
  disabled?: boolean;
};

/**
 * Custom date picker — text input + portal-rendered month-grid calendar.
 * Calendar is portaled to document.body and positioned via
 * `usePopoverPosition` so it escapes overflow constraints (the
 * new-monitoring modal's overflow:auto was clipping the inline calendar).
 *
 * Consumer API unchanged from the pre-portal version.
 */
export function DatePickerField({
  value,
  onChange,
  placeholder = "YYYY-MM-DD",
  ariaLabel = "Date",
  className = "",
  maxDate,
  disabled = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const [triggerEl, setTriggerEl] = useState<HTMLDivElement | null>(null);
  const [popoverEl, setPopoverEl] = useState<HTMLDivElement | null>(null);
  const pos = usePopoverPosition({
    anchorEl: open ? triggerEl : null,
    popoverEl,
    align: "stretch",
  });

  useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent) {
      const inTrigger = triggerEl?.contains(e.target as Node);
      const inPopover = popoverEl?.contains(e.target as Node);
      if (!inTrigger && !inPopover) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, triggerEl, popoverEl]);

  const selectedDate = useMemo(() => parseDate(value), [value]);

  return (
    <div ref={setTriggerEl} className={className}>
      <div
        className={[
          "flex w-full items-center gap-1.5 rounded-[4px] border border-grey-200 bg-white px-3 py-1.5 shadow-flat focus-within:border-grey-500",
          disabled ? "bg-grey-100" : "",
        ].join(" ")}
      >
        <input
          type="text"
          inputMode="numeric"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label={ariaLabel}
          placeholder={placeholder}
          disabled={disabled}
          className="min-w-0 flex-1 border-0 bg-transparent text-body text-grey-900 outline-none placeholder:text-grey-500 disabled:cursor-not-allowed disabled:text-grey-500"
        />
        <button
          type="button"
          onClick={() => setOpen((s) => !s)}
          aria-label={open ? "Close calendar" : "Open calendar"}
          aria-expanded={open}
          disabled={disabled}
          className="flex size-5 items-center justify-center text-grey-500 transition-colors hover:text-black disabled:cursor-not-allowed disabled:hover:text-grey-500"
        >
          <Icon name="calendar_today" size={20} />
        </button>
      </div>
      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <CalendarPopover
            popoverRef={setPopoverEl}
            top={pos?.top ?? 0}
            left={pos?.left ?? 0}
            width={pos?.width}
            ready={!!pos}
            selected={selectedDate}
            maxDate={maxDate === undefined ? new Date() : maxDate}
            onSelect={(d) => {
              onChange(formatDate(d));
              setOpen(false);
            }}
            onClear={() => {
              onChange("");
              setOpen(false);
            }}
          />,
          document.body,
        )}
    </div>
  );
}

/** 12-year block start for the year grid (e.g. 2026 → 2016 if block-aligned). */
function yearBlockStart(year: number): number {
  return year - (((year % 12) + 12) % 12);
}

function CalendarPopover({
  popoverRef,
  top,
  left,
  width,
  ready,
  selected,
  maxDate,
  onSelect,
  onClear,
}: {
  popoverRef: (el: HTMLDivElement | null) => void;
  top: number;
  left: number;
  /** Match the trigger (input) width when provided; falls back to 280. */
  width?: number;
  ready: boolean;
  selected: DateParts | null;
  /** Maximum selectable date (inclusive). `null` = no limit. */
  maxDate: Date | null;
  onSelect: (d: Date) => void;
  onClear: () => void;
}) {
  const [view, setView] = useState<{ year: number; month: number }>(() => {
    if (selected) return { year: selected.year, month: selected.month ?? 0 };
    const today = new Date();
    return { year: today.getFullYear(), month: today.getMonth() };
  });
  // days → click header → years → pick year → months → pick month → days.
  // Year-first navigation is what a date-of-birth picker needs (jump decades
  // in a few clicks instead of stepping months back ~800 times).
  // A year-only value opens on its months so the year reads as selected.
  const [mode, setMode] = useState<"days" | "months" | "years">(() =>
    selected && selected.month === null ? "months" : "days",
  );

  const days = useMemo(() => buildMonthGrid(view.year, view.month), [view]);
  const blockStart = yearBlockStart(view.year);

  // Disabled-state helpers for max-date enforcement (DOB use case: today is the
  // maximum). Each predicate returns true when the candidate is in the future
  // relative to maxDate. `null` maxDate = no limit, all predicates false.
  const maxY = maxDate?.getFullYear() ?? Infinity;
  const maxM = maxDate?.getMonth() ?? 11; // only used when view.year === maxY
  const maxDay = maxDate?.getDate() ?? 31; // only used when view (year, month) === max
  const yearDisabled = (y: number) => y > maxY;
  const monthDisabled = (year: number, m: number) => year > maxY || (year === maxY && m > maxM);
  const dayDisabled = (d: Date) => {
    if (!maxDate) return false;
    const y = d.getFullYear();
    const m = d.getMonth();
    const dd = d.getDate();
    if (y > maxY) return true;
    if (y === maxY && m > maxM) return true;
    if (y === maxY && m === maxM && dd > maxDay) return true;
    return false;
  };

  // Forward navigation guards: disable the chevron-right (and ignore steps)
  // when the next view would expose only future dates.
  const canStepForward = (() => {
    if (mode === "days") {
      const nextMonth = view.month + 1;
      const nextYear = nextMonth > 11 ? view.year + 1 : view.year;
      const m = nextMonth > 11 ? 0 : nextMonth;
      return !monthDisabled(nextYear, m);
    }
    if (mode === "months") return view.year < maxY;
    // years: 12-year block — allow forward only if there is a non-future year
    // in the next block.
    return blockStart + 12 <= maxY;
  })();

  function stepHeader(delta: number) {
    if (delta > 0 && !canStepForward) return;
    if (mode === "days") {
      setView((v) => {
        const m = v.month + delta;
        if (m < 0) return { year: v.year - 1, month: 11 };
        if (m > 11) return { year: v.year + 1, month: 0 };
        return { year: v.year, month: m };
      });
    } else if (mode === "months") {
      setView((v) => ({ ...v, year: v.year + delta }));
    } else {
      setView((v) => ({ ...v, year: v.year + delta * 12 }));
    }
  }

  const headerLabel =
    mode === "days"
      ? `${MONTHS[view.month]} ${view.year}`
      : mode === "months"
        ? `${view.year}`
        : `${blockStart} – ${blockStart + 11}`;
  const stepUnit = mode === "days" ? "month" : mode === "years" ? "years" : "year";

  return (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Date picker"
      style={{ top, left, width: width ?? 280, visibility: ready ? "visible" : "hidden" }}
      /* z-[1400]: above the MUI Dialog (zIndex.modal=1300) so the calendar shows
         in front of the add-monitoring modal, not behind it.
         width matches the trigger (input) via usePopoverPosition align="stretch". */
      className="fixed z-[1400] flex flex-col gap-2 rounded-[4px] border border-grey-200 bg-white p-3 shadow-soft"
    >
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => stepHeader(-1)}
          aria-label={`Previous ${stepUnit}`}
          className="flex size-8 items-center justify-center rounded-[4px] text-grey-900 transition-colors hover:bg-grey-100 hover:text-black"
        >
          <Icon name="chevron_left" size={20} />
        </button>
        <button
          type="button"
          // Tapping the label drills toward year selection (or back to days).
          onClick={() => setMode((m) => (m === "years" ? "days" : "years"))}
          aria-label="Switch calendar view"
          className="rounded-[4px] px-2 py-1 text-small font-medium text-black transition-colors hover:bg-grey-100"
        >
          {headerLabel}
        </button>
        <button
          type="button"
          onClick={() => stepHeader(1)}
          aria-label={`Next ${stepUnit}`}
          disabled={!canStepForward}
          className="flex size-8 items-center justify-center rounded-[4px] text-grey-900 transition-colors hover:bg-grey-100 hover:text-black disabled:cursor-not-allowed disabled:text-grey-300 disabled:hover:bg-transparent disabled:hover:text-grey-300"
        >
          <Icon name="chevron_right" size={20} />
        </button>
      </div>

      {mode === "days" && (
        <>
          <div className="grid grid-cols-7 gap-0.5 text-center text-tiny font-medium text-grey-500">
            {WEEKDAYS.map((w, i) => (
              <div key={i} className="py-1">
                {w}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {days.map((d) => {
              const inMonth = d.getMonth() === view.month;
              const isSelected =
                !!selected &&
                selected.year === d.getFullYear() &&
                selected.month === d.getMonth() &&
                selected.day === d.getDate();
              const disabled = dayDisabled(d);
              return (
                <button
                  key={`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`}
                  type="button"
                  onClick={() => onSelect(d)}
                  aria-pressed={isSelected}
                  disabled={disabled}
                  className={[
                    "flex aspect-square w-full items-center justify-center rounded-[4px] text-small transition-colors",
                    disabled
                      ? "cursor-not-allowed text-grey-300"
                      : isSelected
                        ? "border-t border-grey-900 bg-grey-950 text-white shadow-flat-black"
                        : inMonth
                          ? "text-black hover:bg-grey-100"
                          : "text-grey-500 hover:bg-grey-50",
                  ].join(" ")}
                >
                  {d.getDate()}
                </button>
              );
            })}
          </div>
        </>
      )}

      {mode === "months" && (
        <div className="grid grid-cols-3 gap-1">
          {MONTHS.map((m, i) => {
            const isSelected = !!selected && selected.year === view.year && selected.month === i;
            const disabled = monthDisabled(view.year, i);
            return (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setView((v) => ({ ...v, month: i }));
                  setMode("days");
                }}
                aria-pressed={isSelected}
                disabled={disabled}
                className={[
                  "flex items-center justify-center rounded-[4px] py-2 text-small transition-colors",
                  disabled
                    ? "cursor-not-allowed text-grey-300"
                    : isSelected
                      ? "bg-grey-950 text-white shadow-flat-black"
                      : "text-black hover:bg-grey-100",
                ].join(" ")}
              >
                {m.slice(0, 3)}
              </button>
            );
          })}
        </div>
      )}

      {mode === "years" && (
        <div className="grid grid-cols-3 gap-1">
          {Array.from({ length: 12 }, (_, i) => blockStart + i).map((y) => {
            const isSelected = !!selected && selected.year === y;
            const disabled = yearDisabled(y);
            return (
              <button
                key={y}
                type="button"
                onClick={() => {
                  setView((v) => ({ ...v, year: y }));
                  setMode("months");
                }}
                aria-pressed={isSelected}
                disabled={disabled}
                className={[
                  "flex items-center justify-center rounded-[4px] py-2 text-small transition-colors",
                  disabled
                    ? "cursor-not-allowed text-grey-300"
                    : isSelected
                      ? "bg-grey-950 text-white shadow-flat-black"
                      : "text-black hover:bg-grey-100",
                ].join(" ")}
              >
                {y}
              </button>
            );
          })}
        </div>
      )}

      {/* No "Today" shortcut: this is a date-of-birth field — nobody's birthday
          is today, so a today affordance only invites mistakes (Fakhrul). */}
      <div className="flex items-center justify-end border-t border-grey-100 pt-2">
        <button
          type="button"
          onClick={onClear}
          className="text-small font-medium text-grey-900 transition-colors hover:text-black"
        >
          Clear
        </button>
      </div>
    </div>
  );
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

function buildMonthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1);
  const start = new Date(year, month, 1 - first.getDay());
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) {
    days.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }
  return days;
}

/** Parsed value; `month` (0-based) and `day` are null for partial dates. */
type DateParts = { year: number; month: number | null; day: number | null };

function parseDate(value: string): DateParts | null {
  const m = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/.exec(value.trim());
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const month = m[2] ? parseInt(m[2], 10) - 1 : null;
  const day = m[3] ? parseInt(m[3], 10) : null;
  if (month === null) return { year, month, day: null };
  if (month < 0 || month > 11) return null;
  if (day === null) return { year, month, day };
  const d = new Date(year, month, day);
  if (d.getMonth() !== month || d.getDate() !== day) return null;
  return { year, month, day };
}

function formatDate(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}
